/* eslint-disable react-hooks/exhaustive-deps */
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "../../../firebase";
import { Conversation, Message } from "./message.type";
import { sendBroadcastMessage } from "../../../services/firebaseService";
import { User } from "../user/user.type";

const enrichConversation = async (conversationDoc: any) => {
  const conversationData = {
    id: conversationDoc.id,
    ...conversationDoc.data(),
  } as any;

  const messagesQuery = query(
    collection(db, "conversations", conversationDoc.id, "messages"),
    orderBy("createdAt", "asc"),
  );
  const messagesSnapshot = await getDocs(messagesQuery);

  const messages = await Promise.all(
    messagesSnapshot.docs.map(async (msgDoc) => {
      const msgData = { id: msgDoc.id, ...msgDoc.data() } as any;
      const senderDoc = await getDoc(doc(db, "users", msgData.senderId));
      const sender = senderDoc.exists()
        ? { id: senderDoc.id, ...senderDoc.data() }
        : null;
      return { ...msgData, sender };
    }),
  );

  const initialSenderDoc = await getDoc(
    doc(db, "users", conversationData.initialSenderId),
  );
  const initialSender = initialSenderDoc.exists()
    ? { id: initialSenderDoc.id, ...initialSenderDoc.data() }
    : null;

  const initialReceiverDoc = await getDoc(
    doc(db, "users", conversationData.receiverId),
  );
  const initialReceiver = initialReceiverDoc.exists()
    ? { id: initialReceiverDoc.id, ...initialReceiverDoc.data() }
    : null;

  let classroom = null;
  if (conversationData.classId) {
    const classeDoc = await getDoc(
      doc(db, "classrooms", conversationData.classId),
    );
    classroom = classeDoc.exists()
      ? { id: classeDoc.id, ...classeDoc.data() }
      : null;
  }

  return {
    ...conversationData,
    messages,
    initialSender,
    initialReceiver,
    classroom,
  };
};

const fetchUserDoc = async (userId: string): Promise<User | null> => {
  if (!userId) return null;
  const userDoc = await getDoc(doc(db, "users", userId));
  if (userDoc.exists()) {
    return { uid: userDoc.id, ...userDoc.data() } as User;
  }
  return null;
};

export const MessageApi = createApi({
  reducerPath: "messageApi",
  tagTypes: [
    "messages",
    "conversations",
    "conversationByUser",
    "conversationList",
  ],
  baseQuery: fakeBaseQuery(),
  endpoints: (builder) => ({
    conversationsListByUser: builder.query<Conversation[], void>({
      queryFn: async () => {
        const user = auth.currentUser;
        if (!user) {
          return { error: { message: "Aucun utilisateur connecté." } };
        }
        const userId = user.uid;
        try {
          const q = query(
            collection(db, "conversations"),
            where("participants", "array-contains", userId),
            orderBy("updatedAt", "desc"),
          );
          const snap = await getDocs(q);
          const conversations: Conversation[] = [];


          for (const document of snap.docs) {
            const conv = document.data();


            const [senderProfile, receiverProfile] = await Promise.all([
              fetchUserDoc(conv.initialSenderId),
              fetchUserDoc(conv.receiverId),
            ]);


            const msgQuery = query(
              collection(db, "conversations", document.id, "messages"),
              orderBy("createdAt", "asc"),
            );
            const msgSnap = await getDocs(msgQuery);
            const allMessages = msgSnap.docs.map(
              (msgDoc) =>
                ({
                  id: msgDoc.id,
                  ...msgDoc.data(),
                }) as Message,
            );

            conversations.push({
              id: document.id,
              ...conv,
              initialSender: senderProfile,
              initialReceiver: receiverProfile,
              messages: allMessages,
            } as unknown as Conversation);
          }

          return { data: conversations };
        } catch (error: any) {
          return { error: error.message };
        }
      },
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        let unsubscribe = () => { };
        const user = auth.currentUser;
        const userId = user?.uid;

        try {
          await cacheDataLoaded;

          const q = query(
            collection(db, "conversations"),
            where("participants", "array-contains", userId),
            orderBy("updatedAt", "desc"),
          );

          // Écoute des conversations en temps réel
          unsubscribe = onSnapshot(q, async (snapshot) => {
            const rawConvs = snapshot.docs.map((document) => ({
              id: document.id,
              ...document.data(),
            }));

            const fullyHydratedConversations = await Promise.all(
              rawConvs.map(async (conv: any) => {
                const [senderProfile, receiverProfile] = await Promise.all([
                  fetchUserDoc(conv.initialSenderId),
                  fetchUserDoc(conv.receiverId),
                ]);

                const msgQuery = query(
                  collection(db, "conversations", conv.id, "messages"),
                  orderBy("createdAt", "asc"),
                );
                const msgSnap = await getDocs(msgQuery);

                const allMessages = msgSnap.docs.map(
                  (msgDoc) =>
                    ({
                      id: msgDoc.id,
                      ...msgDoc.data(),
                    }) as Message,
                );

                return {
                  ...conv,
                  initialSender: senderProfile,
                  initialReceiver: receiverProfile,
                  createdBy: senderProfile,
                  messages: allMessages,
                } as Conversation;
              }),
            );

            updateCachedData((draft) => {
              return fullyHydratedConversations;
            });
          });
        } catch (error) {
          console.error("Erreur écouteur conversations:", error);
        }

        await cacheEntryRemoved;
        unsubscribe();
      },
      providesTags: ["conversationByUser"]
    }),
    sendMessage: builder.mutation({
      async queryFn(data) {
        try {
          const response = await sendBroadcastMessage(data);
          return {
            data: response,
            message: "Message envoyé.",
          };
        } catch (error: any) {
          return { error: { status: "CUSTOM", message: error.message } };
        }
      },
    }),
    readMessage: builder.mutation({
      async queryFn({ id, conversationId }) {
        try {
          const user = auth.currentUser;
          if (!user) {
            return { error: { message: "Aucun utilisateur connecté." } };
          }

          const docRef = doc(
            db,
            "conversations",
            conversationId,
            "messages",
            id,
          );
          await updateDoc(docRef, {
            isSeen: true,
            updatedAt: serverTimestamp(),
          });

          return {
            data: {
              success: true,
              message: "Message marqué comme lu !",
            },
          };
        } catch (error: any) {
          return { error: { status: "CUSTOM", message: error.message } };
        }
      },
      invalidatesTags: ["conversationByUser"],
    }),
    deleteConversation: builder.mutation({
      async queryFn(conversationId: string) {
        if (!conversationId)
          return { error: { message: "conversationId manquant" } };
        try {
          // Supprimer tous les messages de la sous-collection
          const messagesSnapshot = await getDocs(
            collection(db, "conversations", conversationId, "messages"),
          );
          await Promise.all(
            messagesSnapshot.docs.map((msgDoc) =>
              deleteDoc(
                doc(db, "conversations", conversationId, "messages", msgDoc.id),
              ),
            ),
          );

          // Supprimer la conversation
          await deleteDoc(doc(db, "conversations", conversationId));

          return {
            data: {
              conversationId,
              message: "Conversation supprimée !",
              success: true,
            },
          };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: ["conversationByUser"],
    }),
  }),
});

export const {
  useConversationsListByUserQuery,
  useSendMessageMutation,
  useReadMessageMutation,
  useDeleteConversationMutation,
} = MessageApi;
